import { useEffect, useRef, useState } from "react";
import { http } from "@/lib/api";
import { useRealtimeTick } from "@/lib/realtime";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { ClipboardCheck, Save, CheckCircle2, Clock, CalendarDays, ListOrdered, ImageDown } from "lucide-react";
import { DayEndCatalogsDialog } from "@/components/DayEndCatalogsDialog";
import { toast } from "sonner";
import { fmtNum } from "@/lib/helpers";
import { useAuth } from "@/context/AuthContext";
import { canAct } from "@/lib/access";
import { FileDown } from "lucide-react";

const trToday = () => new Date(Date.now() + 3 * 3600 * 1000).toISOString().slice(0, 10);
const fmtTime = (iso) => new Date(iso).toLocaleString("tr-TR", { dateStyle: "short", timeStyle: "short", timeZone: "Europe/Istanbul" });
const inputCls = "bg-slate-900/60 border-slate-700 w-44";

function GroupList({ groups, testId, empty = "Bugün işlem yok" }) {
  if (!groups?.length) return <div className="px-4 py-6 text-center text-xs text-slate-600">{empty}</div>;
  return (
    <div className="divide-y divide-slate-800/70" data-testid={testId}>
      {groups.map((g) => (
        <div key={g.id} data-testid={`${testId}-group-${g.id}`} className={`flex items-center gap-2 px-4 py-2 text-sm ${g.qty ? "" : "opacity-50"}`}>
          <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ background: g.color || "#64748b" }} />
          <span className="flex-1 min-w-0 break-words leading-snug font-semibold" title={g.name}>{g.name}</span>
          <b className="tabular-nums text-blue-300 w-14 text-right" data-testid={`${testId}-group-qty-${g.id}`}>{fmtNum(g.qty)}</b>
        </div>
      ))}
    </div>
  );
}

function BranchDayCard({ b, total }) {
  return (
    <Card className={`border-slate-800 bg-slate-900/60 glass overflow-hidden flex flex-col ${total ? "ring-1 ring-blue-500/40" : ""}`} data-testid={`dayend-card-${b.branch_code}`}>
      <div className="px-4 py-3 border-b border-slate-800 flex items-start justify-between gap-2">
        <div>
          <div className="text-[10px] font-mono text-blue-300 uppercase tracking-widest">{b.branch_code}</div>
          <div className="font-display font-bold text-lg">{b.branch_name}</div>
        </div>
        {b.days !== undefined ? (
          <Badge variant="outline" className="border-slate-600 text-slate-300 text-[10px]">{b.days} gün</Badge>
        ) : total ? (
          <Badge variant="outline" className="border-blue-500/40 bg-blue-500/10 text-blue-300 text-[10px]" data-testid="dayend-status-total">{b.closed_count}/{b.branch_count} şube kaydedildi</Badge>
        ) : b.closed ? (
          <Badge variant="outline" className="border-emerald-500/40 bg-emerald-500/10 text-emerald-300 text-[10px]" data-testid={`dayend-status-${b.branch_code}`}>
            <CheckCircle2 size={11} className="mr-1" />{b.closed.auto ? "Otomatik" : b.closed.saved_by_name} · {fmtTime(b.closed.saved_at)}
          </Badge>
        ) : (
          <Badge variant="outline" className="border-amber-500/40 bg-amber-500/10 text-amber-300 text-[10px]" data-testid={`dayend-status-${b.branch_code}`}>
            <Clock size={11} className="mr-1" />Kaydedilmedi
          </Badge>
        )}
      </div>
      <div className="px-4 py-3 grid grid-cols-2 gap-3 border-b border-slate-800">
        <div><div className="text-[10px] uppercase font-mono text-slate-500">Toplam Adet</div>
          <div className="font-display text-3xl font-bold text-blue-300" data-testid={`dayend-total-${b.branch_code}`}>{fmtNum(b.total_qty)}</div></div>
        <div><div className="text-[10px] uppercase font-mono text-slate-500">İşlem</div>
          <div className="font-display text-3xl font-bold">{fmtNum(b.sale_count)}</div></div>
      </div>
      <div className="flex-1">
        <GroupList groups={b.groups} testId={`dayend-groups-${b.branch_code}`} />
      </div>
    </Card>
  );
}

const totalCard = (branches, groups) => ({
  branch_id: "_total", branch_code: "TOPLAM", branch_name: "Tüm Şubeler", groups,
  total_qty: branches.reduce((a, b) => a + b.total_qty, 0), sale_count: branches.reduce((a, b) => a + b.sale_count, 0),
  closed_count: branches.filter((b) => b.closed).length, branch_count: branches.length,
});

function CardGrid({ cards, testId, fixed }) {
  const cols = Math.max(1, Math.min(cards.length, 6));
  return (
    <div className={fixed ? "grid gap-4 p-4" : "grid gap-4 p-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:[grid-template-columns:repeat(var(--cols),minmax(0,1fr))]"}
         style={fixed ? { gridTemplateColumns: `repeat(${cols}, 280px)` } : { "--cols": cols }} data-testid={testId}>
      {cards.map((b) => <BranchDayCard key={b.branch_id} b={b} total={b.branch_id === "_total"} />)}
    </div>
  );
}

function ImageExportDialog({ cards, name, title, onClose }) {
  const [sel, setSel] = useState(() => new Set(cards.map((c) => c.branch_id)));
  const [busy, setBusy] = useState(false);
  const ref = useRef();
  const toggle = (id) => { const n = new Set(sel); n.has(id) ? n.delete(id) : n.add(id); setSel(n); };
  const picked = cards.filter((c) => sel.has(c.branch_id));
  const download = async () => {
    setBusy(true);
    try {
      await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));
      const node = ref.current;
      const { toPng } = await import("html-to-image");
      const url = await toPng(node, { backgroundColor: getComputedStyle(document.body).backgroundColor, pixelRatio: 2, cacheBust: true,
                                      width: Math.ceil(node.getBoundingClientRect().width), height: Math.ceil(node.getBoundingClientRect().height) + 4 });
      const a = document.createElement("a"); a.href = url; a.download = name; a.click();
      onClose();
    } catch { toast.error("Görüntü oluşturulamadı"); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-slate-900 border-slate-800 max-w-sm" data-testid="dayend-image-dialog">
        <DialogHeader><DialogTitle className="font-display">Görüntüye eklenecek kartlar</DialogTitle></DialogHeader>
        <div className="flex gap-3 text-xs">
          <button className="text-blue-300 hover:underline" onClick={() => setSel(new Set(cards.map((c) => c.branch_id)))} data-testid="dayend-image-all">Tümünü seç</button>
          <button className="text-slate-400 hover:underline" onClick={() => setSel(new Set())} data-testid="dayend-image-none">Temizle</button>
        </div>
        <div className="max-h-72 overflow-y-auto rounded-xl border border-slate-800 divide-y divide-slate-800">
          {cards.map((c) => (
            <label key={c.branch_id} className="flex items-center gap-2 px-3 py-2 text-sm cursor-pointer hover:bg-slate-800/40" data-testid={`dayend-image-pick-${c.branch_code}`}>
              <Checkbox checked={sel.has(c.branch_id)} onCheckedChange={() => toggle(c.branch_id)} />
              <span className="flex-1 font-medium">{c.branch_name}</span><span className="text-xs text-slate-500 tabular-nums">{fmtNum(c.total_qty)} adet</span>
            </label>
          ))}
        </div>
        <Button disabled={busy || !picked.length} onClick={download} className="bg-blue-600 hover:bg-blue-500" data-testid="dayend-image-download">
          <ImageDown size={15} className="mr-1.5" /> {busy ? "Hazırlanıyor..." : `${picked.length} kartı indir`}
        </Button>
        <div style={{ position: "fixed", left: -20000, top: 0, pointerEvents: "none" }} aria-hidden>
          <div ref={ref} className="bg-slate-950 text-slate-100 inline-block pb-4" data-testid="dayend-export-node">
            {title && <div className="px-5 pt-4 font-display font-bold text-lg">{title}</div>}
            <CardGrid cards={picked} fixed />
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function DayToolbar({ cards, name, title, onCatalogs }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  return (
    <>
      {user?.role === "koordinator" && onCatalogs && (
        <Button variant="outline" className="border-slate-700" onClick={onCatalogs} data-testid="dayend-catalogs-btn"><ListOrdered size={15} className="mr-1.5" /> Rapor Katalogları</Button>
      )}
      <Button variant="outline" className="border-slate-700" onClick={() => setOpen(true)} data-testid="dayend-image-btn">
        <ImageDown size={15} className="mr-1.5" /> Görüntüyü İndir
      </Button>
      {open && <ImageExportDialog cards={cards} name={name} title={title} onClose={() => setOpen(false)} />}
    </>
  );
}

function TodayTab() {
  const [date, setDate] = useState(trToday());
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [catDlg, setCatDlg] = useState(false);
  const tick = useRealtimeTick((m) => m.path.startsWith("/api/sales") || m.path.startsWith("/api/day-end") || m.path.startsWith("/api/branches"));
  const load = () => http.get(`/day-end/live?date=${date}`).then((r) => setData(r.data));
  useEffect(() => { load(); }, [date, tick]); // eslint-disable-line
  const save = async () => {
    setBusy(true);
    try {
      const { data: r } = await http.post("/day-end/close", { date });
      toast.success(`Gün sonu kaydedildi (${r.closed} şube)`);
      const res = await http.get(`/day-end/live?date=${date}`); setData(res.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Kaydedilemedi"); } finally { setBusy(false); }
  };
  if (!data) return <div className="text-slate-400">Yükleniyor...</div>;
  const total = data.branches.reduce((a, b) => a + b.total_qty, 0);
  const cards = [...(data.branches.length > 1 ? [totalCard(data.branches, data.groups)] : []), ...data.branches];
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <Input type="date" value={date} max={trToday()} onChange={(e) => setDate(e.target.value)} className={inputCls} data-testid="dayend-date" />
        <div className="rounded-xl border border-slate-800 bg-slate-900/60 px-4 py-2 text-sm">Toplam: <b className="text-blue-300 text-lg" data-testid="dayend-grand-total">{fmtNum(total)}</b> adet</div>
        <div className="flex-1" />
        <DayToolbar cards={cards} name={`gun_sonu_${date}.png`} title={`Gün Sonu · ${date.split("-").reverse().join(".")}`} onCatalogs={() => setCatDlg(true)} />
        <Button onClick={save} disabled={busy} className="py-6 px-6 bg-emerald-600 hover:bg-emerald-500 text-base" data-testid="dayend-save">
          <Save size={18} className="mr-2" /> Gün Sonunu Kaydet
        </Button>
      </div>
      <p className="text-xs text-slate-500">Kaydedilmeyen şubelerin gün sonu her akşam 23:00'te otomatik kaydedilir. Tekrar kaydetmek kaydı günceller.</p>
      <CardGrid cards={cards} testId="dayend-grid" />
      {catDlg && <DayEndCatalogsDialog onClose={() => setCatDlg(false)} onSaved={() => { setCatDlg(false); load(); }} />}
    </div>
  );
}

function ReportTab() {
  const [period, setPeriod] = useState("month");
  const [value, setValue] = useState(trToday().slice(0, 7));
  const [branch, setBranch] = useState("");
  const [branches, setBranches] = useState([]);
  const [rep, setRep] = useState(null);
  useEffect(() => { http.get("/day-end/live").then((r) => setBranches(r.data.branches)); }, []);
  useEffect(() => {
    if (!value) return;
    http.get(`/day-end/report?period=${period}&value=${value}${branch ? `&branch_id=${branch}` : ""}`).then((r) => setRep(r.data));
  }, [period, value, branch]);
  const { user } = useAuth();
  const [pdfBusy, setPdfBusy] = useState(false);
  const downloadPdf = async () => {
    setPdfBusy(true);
    try {
      const res = await http.get(`/day-end/report/pdf?period=${period}&value=${value}${branch ? `&branch_id=${branch}` : ""}`, { responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(res.data); a.download = `gun_sonu_${value}.pdf`; a.click();
    } catch { toast.error("PDF oluşturulamadı"); } finally { setPdfBusy(false); }
  };
  const changePeriod = (p) => { setPeriod(p); const t = trToday(); setValue(p === "day" ? t : p === "month" ? t.slice(0, 7) : t.slice(0, 4)); };
  const repCards = !rep?.branches?.length ? [] : [
    ...(rep.branches.length > 1 ? [{ ...totalCard(rep.branches, rep.groups), days: Math.max(...rep.branches.map((x) => x.days)) }] : []), ...rep.branches];
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2 items-center">
        {[["day", "Günlük"], ["month", "Aylık"], ["year", "Yıllık"]].map(([k, l]) => (
          <Button key={k} variant={period === k ? "default" : "outline"} onClick={() => changePeriod(k)}
                  className={period === k ? "bg-blue-600 hover:bg-blue-500" : "border-slate-700"} data-testid={`dayend-period-${k}`}>{l}</Button>
        ))}
        {period === "day" && <Input type="date" value={value} onChange={(e) => setValue(e.target.value)} className={inputCls} data-testid="dayend-report-value" />}
        {period === "month" && <Input type="month" value={value} onChange={(e) => setValue(e.target.value)} className={inputCls} data-testid="dayend-report-value" />}
        {period === "year" && <Input type="number" min="2024" max="2100" value={value} onChange={(e) => setValue(e.target.value)} className={inputCls} data-testid="dayend-report-value" />}
        {branches.length > 1 && (
          <select value={branch} onChange={(e) => setBranch(e.target.value)} className="h-10 rounded-md bg-slate-900/60 border border-slate-700 px-3 text-sm" data-testid="dayend-report-branch">
            <option value="">Tüm şubeler</option>
            {branches.map((b) => <option key={b.branch_id} value={b.branch_id}>{b.branch_name}</option>)}
          </select>
        )}
        <div className="flex-1" />
        {repCards.length > 0 && <DayToolbar cards={repCards} name={`gun_sonu_${value}.png`} title={`Gün Sonu Raporu · ${value}`} />}
        {canAct(user, "export") && (
          <Button onClick={downloadPdf} disabled={pdfBusy || !value} variant="outline" className="border-red-500/50 text-red-200 hover:bg-red-500/10" data-testid="dayend-report-pdf">
            <FileDown size={15} className="mr-1.5" /> {pdfBusy ? "Hazırlanıyor..." : "PDF İndir"}
          </Button>
        )}
      </div>
      {rep && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
            <Card className="border-slate-800 bg-slate-900/60"><CardContent className="p-4"><div className="text-[10px] uppercase font-mono text-slate-500">Toplam Adet</div><div className="font-display text-3xl font-bold text-blue-300" data-testid="dayend-report-total">{fmtNum(rep.total_qty)}</div></CardContent></Card>
            <Card className="border-slate-800 bg-slate-900/60"><CardContent className="p-4"><div className="text-[10px] uppercase font-mono text-slate-500">İşlem Sayısı</div><div className="font-display text-3xl font-bold">{fmtNum(rep.sale_count)}</div></CardContent></Card>
            <Card className="border-slate-800 bg-slate-900/60"><CardContent className="p-4"><div className="text-[10px] uppercase font-mono text-slate-500">Kaydedilen Gün</div><div className="font-display text-3xl font-bold">{fmtNum(new Set(rep.buckets.map((b) => b.key)).size)}</div></CardContent></Card>
          </div>
          {rep.branches.length > 0 ? (
            <CardGrid cards={repCards} testId="dayend-report-grid" />
          ) : <Card className="border-slate-800 bg-slate-900/60"><div className="px-4 py-10 text-center text-sm text-slate-500">Bu dönem için kaydedilmiş gün sonu yok</div></Card>}
          {period !== "day" && rep.buckets.length > 0 && (
            <Card className="border-slate-800 bg-slate-900/60 glass" data-testid="dayend-report-buckets">
              <CardContent className="p-4 space-y-1.5">
                <div className="text-xs font-mono uppercase text-slate-500 mb-2 flex items-center gap-1.5"><CalendarDays size={13} /> {period === "month" ? "Günlere göre" : "Aylara göre"}</div>
                {rep.buckets.map((k) => {
                  const max = Math.max(...rep.buckets.map((x) => x.total_qty), 1);
                  return (
                    <div key={k.key} className="flex items-center gap-3 text-sm">
                      <span className="w-24 font-mono text-xs text-slate-400">{k.key}</span>
                      <div className="flex-1 h-5 rounded bg-slate-800 overflow-hidden"><div className="h-full bg-blue-600/70" style={{ width: `${(k.total_qty / max) * 100}%` }} /></div>
                      <b className="w-16 text-right tabular-nums">{fmtNum(k.total_qty)}</b>
                    </div>
                  );
                })}
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
}

export default function DayEnd() {
  return (
    <div className="space-y-6">
      <header className="animate-fade-up">
        <h1 className="font-display text-3xl font-bold flex items-center gap-2"><ClipboardCheck className="text-emerald-400" /> Gün Sonu Raporu</h1>
        <p className="text-slate-400 text-sm mt-1">Şube şube günlük işlem adetleri, gün sonu kaydı ve geriye dönük raporlar</p>
      </header>
      <Tabs defaultValue="today">
        <TabsList className="bg-slate-900/60 border border-slate-800">
          <TabsTrigger value="today" data-testid="dayend-tab-today">Bugün</TabsTrigger>
          <TabsTrigger value="report" data-testid="dayend-tab-report">Geçmiş Raporlar</TabsTrigger>
        </TabsList>
        <TabsContent value="today" className="mt-4"><TodayTab /></TabsContent>
        <TabsContent value="report" className="mt-4"><ReportTab /></TabsContent>
      </Tabs>
    </div>
  );
}
